import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import api from "@/lib/api";
import { formatApiErrorDetail } from "@/lib/formatError";
import { jsPDF } from "jspdf";
import { FileEdit, Sparkles, Download, Send, Pencil, RotateCcw, CheckCircle2 } from "lucide-react";

export default function TailoredResume() {
  const location = useLocation();
  const prefill = location.state || {};
  const [jd, setJd] = useState(prefill.jd || "");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [result, setResult] = useState(null);
  const [editing, setEditing] = useState(false);
  const [edited, setEdited] = useState("");
  const [used, setUsed] = useState(false);
  const navigate = useNavigate();

  const generate = async () => {
    if (jd.trim().length < 50) { setErr("Please paste the complete job description (min 50 characters)."); return; }
    setErr(""); setLoading(true);
    try {
      const r = await api.post("/resume/tailor-full", { job_description: jd });
      setResult(r.data);
      setEdited(r.data.tailored_resume || "");
    } catch (e) {
      const detail = e.response?.data?.detail;
      const friendly = (!detail || typeof e.response?.data === "string" || e.response?.status >= 500)
        ? "The AI service is temporarily unavailable. Please try again in a few minutes."
        : formatApiErrorDetail(detail);
      setErr(friendly);
    } finally { setLoading(false); }
  };

  const download = () => {
    const text = edited || result?.tailored_resume || "";
    const doc = new jsPDF({ unit: "pt", format: "a4" });
    const pageW = doc.internal.pageSize.getWidth();
    const pageH = doc.internal.pageSize.getHeight();
    const margin = 48;
    const maxW = pageW - margin * 2;
    let y = margin;

    const ensureSpace = (needed) => {
      if (y + needed > pageH - margin) { doc.addPage(); y = margin; }
    };

    // Title header
    doc.setFont("helvetica", "bold");
    doc.setFontSize(16);
    doc.setTextColor(60, 40, 160);
    doc.text(`Resume — ${result?.job_title || "Tailored"}`, margin, y);
    y += 10;
    doc.setDrawColor(139, 92, 246);
    doc.setLineWidth(1.2);
    doc.line(margin, y, pageW - margin, y);
    y += 20;
    doc.setTextColor(30, 30, 30);

    const isHeading = (line) => {
      const t = line.trim().replace(/^#+\s*/, "").replace(/[:*]+$/, "");
      if (!t || t.length > 40) return false;
      return /^#+\s/.test(line.trim()) || (t === t.toUpperCase() && /[A-Z]/.test(t)) ||
        /^(summary|professional summary|experience|work experience|skills|technical skills|education|projects|certifications|achievements)$/i.test(t);
    };

    text.split("\n").forEach((raw) => {
      const line = raw.replace(/\*\*/g, "").replace(/^#+\s*/, "");
      if (!line.trim()) { y += 6; return; }
      if (isHeading(raw)) {
        ensureSpace(34);
        y += 10;
        doc.setFont("helvetica", "bold");
        doc.setFontSize(12);
        doc.setTextColor(60, 40, 160);
        doc.text(line.trim().replace(/[:]+$/, "").toUpperCase(), margin, y);
        y += 6;
        doc.setDrawColor(210, 200, 240);
        doc.setLineWidth(0.6);
        doc.line(margin, y, pageW - margin, y);
        y += 14;
        doc.setTextColor(30, 30, 30);
        return;
      }
      doc.setFont("helvetica", line.trim().startsWith("-") || line.trim().startsWith("•") ? "normal" : "normal");
      doc.setFontSize(10);
      const wrapped = doc.splitTextToSize(line, maxW);
      wrapped.forEach((w) => {
        ensureSpace(14);
        doc.text(w, margin, y);
        y += 14;
      });
    });

    doc.save(`Tailored_Resume_${(result?.job_title || "job").replace(/\s+/g, "_")}.pdf`);
  };

  const useForApplication = async () => {
    try {
      await api.post("/resume/tailored/use", { tailor_id: result.tailor_id, edited_resume: editing ? edited : undefined });
      setUsed(true);
      setTimeout(() => navigate("/applications"), 1200);
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || "Could not save draft");
    }
  };

  const startOver = () => { setResult(null); setJd(""); setErr(""); setEditing(false); setUsed(false); };

  if (!result) {
    return (
      <div className="max-w-3xl space-y-4" data-testid="tailored-resume-page">
        <div className="jr-card">
          <div className="flex items-center gap-2 text-white font-semibold mb-1">
            <FileEdit className="w-5 h-5 text-violet-300" /> Step 1 — Paste the job description
          </div>
          <p className="text-sm text-zinc-400 mb-4">From LinkedIn, Naukri, a company site — anywhere. The AI rewrites your saved resume for this exact job.</p>
          {prefill.jd && jd === prefill.jd && (
            <div className="mb-3 text-xs text-emerald-300 bg-emerald-500/10 border border-emerald-500/30 rounded-lg px-3 py-2" data-testid="prefill-banner">
              Pre-filled from your job match{prefill.jobTitle ? `: ${prefill.jobTitle}` : ""}{prefill.company ? ` @ ${prefill.company}` : ""}. Review and hit Generate.
            </div>
          )}
          <textarea
            value={jd}
            onChange={(e) => setJd(e.target.value)}
            placeholder="Paste the job description or JD here..."
            rows={12}
            className="jr-input w-full resize-y font-mono text-sm"
            data-testid="jd-input"
          />
          <div className="flex items-center justify-between mt-3">
            <span className="text-xs text-zinc-500" data-testid="jd-char-count">{jd.length} characters</span>
            <button onClick={generate} disabled={loading} className="jr-btn-primary" data-testid="generate-tailored-btn">
              <Sparkles className="w-4 h-4 inline mr-1.5" />
              {loading ? "Tailoring with Claude…" : "Generate Tailored Resume"}
            </button>
          </div>
          {loading && <div className="mt-3 text-xs text-violet-300 jr-pulse" data-testid="tailor-progress">Analyzing JD keywords and rewriting your resume — ~20 seconds…</div>}
          {err && <div className="mt-3 text-sm text-rose-300" data-testid="tailor-error">{err}</div>}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="tailored-result-view">
      <div className="jr-card flex flex-wrap items-center gap-3 justify-between">
        <div>
          <div className="text-white font-semibold">Step 2 — {result.job_title || "Tailored resume"}{result.company ? ` @ ${result.company}` : ""}</div>
          <div className="text-xs text-zinc-400 mt-1">
            {(result.keywords_injected || []).length} keywords injected:{" "}
            <span className="text-emerald-300">{(result.keywords_injected || []).slice(0, 8).join(", ")}</span>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <button onClick={download} className="jr-btn-ghost text-xs py-2 px-3" data-testid="download-tailored-btn"><Download className="w-3.5 h-3.5 inline mr-1" />Download PDF</button>
          <button onClick={() => setEditing(!editing)} className="jr-btn-ghost text-xs py-2 px-3" data-testid="edit-tailored-btn"><Pencil className="w-3.5 h-3.5 inline mr-1" />{editing ? "Done Editing" : "Edit Manually"}</button>
          <button onClick={useForApplication} className="jr-btn-primary text-xs py-2 px-3" data-testid="use-for-application-btn"><Send className="w-3.5 h-3.5 inline mr-1" />Use for Application</button>
          <button onClick={startOver} className="jr-btn-ghost text-xs py-2 px-3" data-testid="start-over-btn"><RotateCcw className="w-3.5 h-3.5 inline mr-1" />Start Over</button>
        </div>
      </div>

      {used && (
        <div className="jr-card border border-emerald-500/40 text-emerald-300 text-sm flex items-center gap-2" data-testid="used-confirmation">
          <CheckCircle2 className="w-4 h-4" /> Saved as a draft application — taking you to your Applications tracker…
        </div>
      )}
      {err && <div className="jr-card border border-rose-500/40 text-rose-300 text-sm">{err}</div>}

      {(result.changes_summary || []).length > 0 && (
        <div className="jr-card" data-testid="changes-summary">
          <div className="text-sm font-semibold text-white mb-2">What the AI changed</div>
          <ul className="text-sm text-zinc-400 space-y-1 list-disc pl-5">
            {result.changes_summary.map((c, i) => <li key={i}>{c}</li>)}
          </ul>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="jr-card" data-testid="original-resume-panel">
          <div className="text-sm font-semibold text-zinc-400 mb-3">Original Resume</div>
          <pre className="whitespace-pre-wrap text-xs text-zinc-500 font-mono max-h-[560px] overflow-y-auto">{result.original_resume}</pre>
        </div>
        <div className="jr-card border border-violet-500/30" data-testid="tailored-resume-panel">
          <div className="text-sm font-semibold text-violet-300 mb-3">✨ Tailored Resume</div>
          {editing ? (
            <textarea value={edited} onChange={(e) => setEdited(e.target.value)} rows={24}
              className="jr-input w-full resize-y font-mono text-xs" data-testid="tailored-edit-area" />
          ) : (
            <pre className="whitespace-pre-wrap text-xs text-zinc-200 font-mono max-h-[560px] overflow-y-auto">{edited || result.tailored_resume}</pre>
          )}
        </div>
      </div>
    </div>
  );
}
